import { api } from '../lib/client';

const DEFAULT_PAGE_SIZE = 20;

/**
 * Fetch all trunks for the current organisation (paginated; fetches all pages).
 * @returns {Promise<{ id: string, name?: string, handler?: string, outbound: boolean }[]>}
 */
export async function fetchTrunks() {
  const items = [];
  let nextOffset = 0;
  do {
    const { data } = await api.get('/trunks', {
      params: { offset: nextOffset, pageSize: 200 }
    });
    items.push(...(data.items || []));
    nextOffset = data.nextOffset != null ? data.nextOffset : null;
  } while (nextOffset != null);
  return items;
}

/**
 * Fetch a page of E.164 DDI numbers with optional filters.
 * @param {Object} opts
 * @param {number} [opts.offset=0]
 * @param {number} [opts.pageSize=20]
 * @param {string} [opts.search]
 * @param {string} [opts.handler]
 * @param {string[]} [opts.trunkIds] - filter by trunk ID(s); when empty/omitted, no trunk filter
 * @returns {Promise<{ items: Array<{ number: string, handler: string, outbound: boolean, trunkId: string|null, createdAt: string|null, callReceived?: string|null, inUse: boolean, agentId?: string|null, agentName?: string|null, provisioned: boolean }>, nextOffset: number|null }>}
 */
export async function fetchNumbers({ offset = 0, pageSize = DEFAULT_PAGE_SIZE, search, handler, trunkIds } = {}) {
  const params = {
    type: 'e164-ddi',
    offset,
    pageSize
  };
  if (search != null && String(search).trim()) params.search = String(search).trim();
  if (handler != null && String(handler).trim()) params.handler = String(handler).trim();
  if (Array.isArray(trunkIds) && trunkIds.length > 0) {
    params.trunkId = trunkIds.map((id) => String(id).trim()).filter(Boolean);
  }
  const { data } = await api.get('/phone-endpoints', { params });
  return { items: data.items || [], nextOffset: data.nextOffset ?? null };
}

/**
 * Create a single E.164 DDI number on a trunk.
 * @param {string} trunkId
 * @param {string} number - E.164 number (with or without +)
 * @param {boolean} [outbound=false] - allow outbound calls (cannot exceed trunk's capability)
 * @returns {Promise<{ success: boolean, number?: string }>}
 */
export async function createNumberForTrunk(trunkId, number, outbound = false) {
  const { data } = await api.post('/phone-endpoints', {
    type: 'e164-ddi',
    trunkId,
    number: String(number).trim(),
    outbound: !!outbound
  });
  return data;
}

/**
 * Create multiple numbers for a trunk. Calls createNumberForTrunk for each; stops on first error unless all fail.
 * @param {string} trunkId
 * @param {string[]} numbers - E.164 numbers (with or without +)
 * @param {boolean} [outbound=false] - allow outbound for all created numbers (cannot exceed trunk's capability)
 * @returns {Promise<{ created: string[], errors: Array<{ number: string, error: string }> }>}
 */
export async function createNumbersForTrunk(trunkId, numbers, outbound = false) {
  const created = [];
  const errors = [];
  for (const num of numbers) {
    try {
      await createNumberForTrunk(trunkId, num, outbound);
      created.push(num);
    } catch (err) {
      const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed';
      errors.push({ number: num, error: message });
    }
  }
  return { created, errors };
}

/**
 * Update an E.164 DDI number (e.g. outbound flag).
 * @param {string} identifier - phone number (E.164, with or without +)
 * @param {{ outbound?: boolean, handler?: string }} data - fields to update
 * @returns {Promise<{ success: boolean }>}
 */
export async function updatePhoneNumber(identifier, data) {
  const { data: res } = await api.put(`/phone-endpoints/${encodeURIComponent(identifier)}`, data);
  return res;
}

/**
 * Delete an E.164 DDI number.
 * @param {string} identifier - phone number (E.164, with or without +)
 * @returns {Promise<{ success: boolean }>}
 */
export async function deletePhoneNumber(identifier) {
  const { data: res } = await api.delete(`/phone-endpoints/${encodeURIComponent(identifier)}`);
  return res;
}
